import { useEffect } from "react";

import { useTranslation } from "react-i18next";
import * as CookieConsent from "vanilla-cookieconsent";
import { CookieConsentConfig } from "vanilla-cookieconsent";

import {
  CAT_ANALYTICS,
  CAT_ERROR_TRACKING,
  CAT_FUNCTIONALITY,
  CAT_MARKETING,
  CAT_NECESSARY,
  SERVICE_AD_PERSONALIZATION,
  SERVICE_AD_STORAGE,
  SERVICE_AD_USER_DATA,
  SERVICE_ANALYTICS_STORAGE,
  SERVICE_GET_RESPONSE,
  SERVICE_SENTRY,
  updateGetResponseConsent,
  updateGtagConsentFromCookieConsent,
  updateSentryConsent,
} from "@/lib/consent";

import en from "../../locales/en.json";
import pl from "../../locales/pl.json";
import uk from "../../locales/uk.json";

import "vanilla-cookieconsent/dist/cookieconsent.css";

const updateConsent = () => {
  updateGtagConsentFromCookieConsent(CookieConsent);
  updateSentryConsent(CookieConsent);
  updateGetResponseConsent(CookieConsent);
};

const getConfig = ({ language }: { language: string }) => {
  const config: CookieConsentConfig = {
    onConsent: updateConsent,
    onChange: updateConsent,
    revision: 1,
    categories: {
      [CAT_NECESSARY]: {
        enabled: true,
        readOnly: true,
      },
      [CAT_ERROR_TRACKING]: {
        enabled: true,
        autoClear: {
          cookies: [],
          reloadPage: true,
        },
        services: {
          [SERVICE_SENTRY]: {
            label: "Sentry",
          },
        },
      },
      [CAT_FUNCTIONALITY]: {},
      [CAT_ANALYTICS]: {
        autoClear: {
          cookies: [{ name: /^_ga/ }, { name: "_gid" }],
        },
        services: {
          [SERVICE_ANALYTICS_STORAGE]: {
            label: "Google Analytics",
          },
        },
      },
      [CAT_MARKETING]: {
        autoClear: {
          cookies: [{ name: /^gaDomain/ }, { name: /^gaVisitor/ }],
          reloadPage: true,
        },
        services: {
          [SERVICE_GET_RESPONSE]: {
            label: "GetResponse",
          },
          [SERVICE_AD_STORAGE]: {
            label: "Google Ad storage",
          },
          [SERVICE_AD_USER_DATA]: {
            label: "Google Ad user data",
          },
          [SERVICE_AD_PERSONALIZATION]: {
            label: "Google Ad personalization",
          },
        },
      },
    },
    language: {
      default: language,
      translations: {
        en: en.translation.cookieConsent,
        pl: pl.translation.cookieConsent,
        uk: uk.translation.cookieConsent,
      },
    },
  };

  return config;
};

const isInIframe = () => {
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
};

export const CookieBanner = () => {
  const { i18n } = useTranslation();

  useEffect(() => {
    if (isInIframe()) return;
    // Uncomment the following line to reset the consent cookie:
    // CookieConsent.reset(true);
    CookieConsent.setLanguage(i18n.language);
    CookieConsent.run(getConfig({ language: i18n.language }));
  }, [i18n.language]);

  return null;
};
